import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";

import { fetchClientById, deleteClientById } from "../api/clients";
import { messageFromError } from "../../../shared/api/drfErrors";

import ClientContactsSection from "./ClientContactsSection";
import ClientPreferencesSection from "./ClientPreferencesSection";
import ClientMatchesSection from "./ClientMatchesSection";
import ClientViewingsSection from "./ClientViewingsSection";
import ClientActivitySection from "./ClientActivitySection";
import ClientTasksSection from "./ClientTasksSection";
import ClientSharesCard from "./ClientSharesCard";
import ClientPresentationsCard from "./ClientPresentationsCard";
import ClientNotesCard from "./ClientNotesCard";
import ClientStampsCard from "./ClientStampsCard";
import ClientEditModal from "./ClientEditModal";
import ClientDeleteConfirmModal from "./ClientDeleteConfirmModal";
import PersonalTransferPreviewCard from "../../companyClients/widgets/PersonalTransferPreviewCard";

// ClientDetailScreenContent — parent-resource widget for
// /clients/:clientId. The canonical client resource surface.
//
// Owns (Phase R4 narrowed scope, preserved through S1 visual transfer):
//   * client core identity (single fetchClientById on mount)
//   * route validity (404 / 403 / error gates apply ONLY to the core)
//   * top-level stale signal lifecycle (consumes location.state.mayBeStale,
//     clears it on its own mount-fetch success — R3)
//   * top-level "Оновити" button — refreshes client core ONLY
//
// Does NOT own (Phase R4 — narrowed from R2):
//   * contacts / communications data — each section owns its own
//     hydration, loading state, and refresh action
//   * cross-section coordination — sections do not know about each other
//   * mutations — children at /edit, /contacts/edit, /activity own them
//
// Phase S1: visual transfer to clientDetail.html design. Layout becomes
// header card + two-column grid (main + sidebar). Header gains avatar +
// pills + meta + action row. Sidebar gains Notes and Stamps cards
// (replacing the former ClientSummarySection). Per CLAUDE.md T-5
// explicit-affordance topology: header actions are literal JSX with
// literal onClick.
//
// Phase S6 — T-3 cross-trigger validation (architecture-freeze-r8.md
// §4 T-3). The parent header gains ONE additional literal button
// ("Залогувати дзвінок") whose onClick invokes a single-method
// imperative bridge on the activity section. The parent:
//
//   * does NOT own the communications slice
//   * does NOT own the modal lifecycle
//   * does NOT own refresh on success
//   * does NOT observe the section's response
//
// The parent's button is a presentation alias — the section's own
// "+ Залогувати" affordance remains the primary entry point and is
// what makes the bridge deletable (A-1 §6.4 rule 6). Removing the
// ref + the alias button would not remove the only entry-point.
//
// Bridge surface, in full:
//     activitySectionRef.current?.openCommunicationModal()
//
// One method. Void. No args. No await. No fanout. No registry.
//
// Phase C1 — T-3 cross-trigger continuation for viewings (R8 §4 T-3).
// The parent header gains ONE literal "Запланувати перегляд" button
// (gated on client.can_edit !== false) whose onClick invokes a
// single-method imperative bridge on the viewings section:
//
//     viewingsSectionRef.current?.openViewingModal()
//
// One method. Void. No args. No await. No fanout. No registry.
// Deletable: the section also renders its own "+ Запланувати перегляд"
// trigger; removing the ref + alias button does not remove the only
// entry-point (A-1 §6.4 rule 6). Independent ref — parent never
// collects refs into a map.
//
// Phase S8 — T-3 cross-trigger continuation for tasks (R8 §4 T-3).
// The parent header gains ONE additional literal button ("Нова
// задача") whose onClick invokes a single-method imperative bridge
// on the tasks section. The bridge sibling of the S6 communications
// bridge — same six structural constraints, independently auditable:
//
//     tasksSectionRef.current?.openTaskModal()
//
// One method. Void. No args. No await. No fanout. No registry.
// Deletable: the section also renders its own "+ Нова задача"
// trigger; removing the ref + alias button does not remove the
// only entry-point (A-1 §6.4 rule 6). Each section holds its own
// ref; the parent never collects refs into a map, never calls more
// than one section ref in a single user gesture (A-1 §6.4 rules
// 4 + 5).
//
// Phase B3 — T-3 cross-trigger continuation for shares (R8 §4
// T-3). The parent header gains ONE literal "Поділитися" button
// (gated on client.is_owner — mirrors backend IsClientOwnerOnly on
// all four share endpoints, B2 F-F) whose onClick invokes a
// single-method imperative bridge on the sidebar shares card:
//
//     sharesCardRef.current?.openShareModal()
//
// One method. Void. No args. No await. No fanout. No registry.
// Deletable: the card ALSO renders its own "+" trigger in its
// __cd-card-head; removing the ref + alias button does not remove
// the only entry-point (A-1 §6.4 rule 6). Independent ref —
// parent never collects refs into a map, never calls more than one
// section ref in a single user gesture (A-1 §6.4 rules 4 + 5).
//
// The shares card is NOT the same section class as the activity /
// tasks sections (those live in the main column and own
// communications / tasks slices). The shares card lives in the
// sidebar and owns the `shares` slice. Different slice, different
// surface; same T-2/T-3 ownership shape (R8 §4 T-2 + T-3).
//
// Phase S7 — T-4 destructive workflow (architecture-freeze-r8.md
// §4 T-4). The parent header gains ONE literal "Видалити" button
// (gated on client.is_owner) whose onClick opens a feature-local
// confirmation modal. On confirm: DELETE → navigate('/clients',
// { replace: true, state: { mayBeStale: true, reason: ... } }).
//
// The parent is the workflow owner because the resource being
// destroyed IS the parent's slice (R5 / R8 L-1 — data-slice owner
// holds the destructive lifecycle). Topology slots:
//
//   * Trigger        — parent header (is_owner gated)
//   * Confirmation   — ClientDeleteConfirmModal (feature-local)
//   * Mutation       — deleteClientById(clientId) → DELETE 204
//   * Successor      — navigate('/clients', { replace: true,
//                       state: { mayBeStale: true, reason } })
//   * Recovery       — /clients consumes mayBeStale once and
//                       refetches (existing R3 protocol)
//   * Cross-surface  — NONE (no eviction, no event emit, no patch
//                       merge, no shared cache)
//   * Optimistic     — NONE
//
// Forbidden patterns explicitly NOT present in this workflow per
// I6-proposed §6.5 (R8 A-2): no generic ConfirmDelete primitive;
// no payload in mayBeStale beyond the doctrinally-allowed reason
// string (already used by the edit flow); no cascade pre-flight
// fetch; no entity_deleted event; no undo affordance; no pre-
// emptive permission eviction.

const TYPE_LABELS = {
  buyer: "Покупець",
  seller: "Продавець",
  both: "Покупець і продавець",
};

const TYPE_ICONS = {
  buyer: "fa-solid fa-cart-shopping",
  seller: "fa-solid fa-house",
  both: "fa-solid fa-arrows-left-right",
};

const STATUS_LABELS = {
  lead: "Лід",
  active: "Активний",
  inactive: "Неактивний",
  closed: "Закритий",
};

const fullnameOf = (client) => {
  if (!client) return "";
  if (typeof client.fullname === "string" && client.fullname.trim().length > 0) {
    return client.fullname;
  }
  const composed = [client.first_name, client.last_name]
    .filter((part) => typeof part === "string" && part.trim().length > 0)
    .join(" ")
    .trim();
  return composed || client.phone || "Без імені";
};

const initialsOf = (client) => {
  if (!client) return "?";
  const first = (client.first_name || "").trim();
  const last = (client.last_name || "").trim();
  if (first || last) {
    return `${first[0] || ""}${last[0] || ""}`.toUpperCase() || "?";
  }
  const composed = fullnameOf(client).trim();
  return (composed[0] || "?").toUpperCase();
};

const truncateId = (id) => {
  if (id == null) return "";
  const s = String(id);
  if (s.length <= 10) return s;
  return `${s.slice(0, 8)}…`;
};

// Phase S12 — D3 R2-c avatar color identity restoration.
// Deterministic palette selector for the parent header's XL avatar.
// Same palette + same modulo logic intentionally duplicated in
// [[ClientSharesCard]] (N=2 — below R7 §7.3 three-caller extraction
// floor; CLAUDE.md anti-abstraction discipline). Palette order is
// the design's `modernize.css:4091-4114` declaration order; do not
// reorder without updating both consumers (drift would render the
// same person in different colors across surfaces).
const AVATAR_COLORS = ["blue", "green", "orange", "purple", "rose", "teal"];
const avatarColorClass = (seed) => {
  const n = Number(seed);
  const idx = Number.isFinite(n) ? Math.abs(n) % AVATAR_COLORS.length : 0;
  return `avatar--${AVATAR_COLORS[idx]}`;
};

function ClientDetailScreenContent() {
  const { clientId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);

  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);

  const [stale, setStale] = useState(false);
  const [staleReason, setStaleReason] = useState(null);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  // Parent-owned T-1 modal state (R8 §4 T-1). Local useState only —
  // no shared store, no registry, no provider.
  const [editOpen, setEditOpen] = useState(false);

  // Preferences editor open + revision counter for matches refresh
  // (CRM-CLIENT-CONTACTS-MATCHING-A1). Local useState only.
  const [prefsEditorOpen, setPrefsEditorOpen] = useState(false);
  const [preferencesRevision, setPreferencesRevision] = useState(0);

  // Parent-owned T-4 destructive workflow state (R8 §4 T-4). Local
  // useState only — no shared store, no destructive coordinator,
  // no entity lifecycle registry. The workflow lives entirely in
  // this component until the DELETE returns 204 and React Router
  // unmounts the route.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // R8.7 B-1 — Closure-race guard for destructive double-click. Read
  // synchronously inside handleDeleteConfirm; flipped before any
  // await. The useState `deleting` above remains the source of truth
  // for the disabled prop on the modal confirm button — the ref ONLY
  // closes the gap between the first click and the next render where
  // the state-derived disable applies.
  const deletingRef = useRef(false);

  // T-3 bridge handles (R8 §4 T-3). Each ref points at one section's
  // imperative surface — each exposing exactly one method:
  //   * viewingsSectionRef → openViewingModal()     (C1)
  //   * activitySectionRef → openCommunicationModal()  (S6)
  //   * tasksSectionRef    → openTaskModal()           (S8)
  //   * sharesCardRef      → openShareModal()          (B3)
  //
  // Independent refs, independent sections, independent bridges. The
  // parent never collects them into a map, never iterates over them,
  // and never calls more than one in a single user gesture (A-1 §6.4
  // rules 4 + 5). Each ref is independently deletable.
  const viewingsSectionRef = useRef(null);
  const activitySectionRef = useRef(null);
  const tasksSectionRef = useRef(null);
  const sharesCardRef = useRef(null);

  // Client-core fetch generation + AbortController. Preferences save
  // increments the generation so a late stale refresh cannot overwrite
  // a just-reconciled preferences snapshot.
  const clientFetchGenerationRef = useRef(0);
  const clientFetchControllerRef = useRef(null);
  // Synchronous in-flight guard — state `refreshing` alone cannot clear
  // the early-return hole when save lands during a pending refresh.
  const refreshingRef = useRef(false);
  // Holds the latest server-confirmed preferences until the next
  // successful client-core fetch applies them (guards optional reload).
  const preferencesReconcileRef = useRef(null);

  useEffect(() => {
    if (clientId == null) {
      setLoadState("not-found");
      return undefined;
    }

    if (clientFetchControllerRef.current) {
      clientFetchControllerRef.current.abort();
    }
    const controller = new AbortController();
    clientFetchControllerRef.current = controller;
    const generation = ++clientFetchGenerationRef.current;

    setLoadState("loading");
    setLoadError(null);

    fetchClientById(clientId, { signal: controller.signal })
      .then((clientData) => {
        if (controller.signal.aborted) return;
        if (generation !== clientFetchGenerationRef.current) return;
        setClient(clientData);
        setLoadState("loaded");
        // Phase R3 — the mount-fetch IS the explicit refresh for the
        // parent's slice of data (client core). If `mayBeStale`
        // arrived on this navigation, the consumption useEffect set
        // `stale=true` while the body was gated by loadState="loading".
        // Clearing it here ensures the banner never surfaces once the
        // parent's owned data has actually landed. Phase R4: the
        // signal is now scoped to "client core may be stale"; sections
        // handle their own freshness independently.
        setStale(false);
        setStaleReason(null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (generation !== clientFetchGenerationRef.current) return;
        const status = err && err.response && err.response.status;
        if (status === 404) {
          setLoadState("not-found");
        } else if (status === 403) {
          setLoadState("forbidden");
        } else {
          setLoadError(messageFromError(err));
          setLoadState("error");
        }
      })
      .finally(() => {
        if (clientFetchControllerRef.current === controller) {
          clientFetchControllerRef.current = null;
        }
      });

    return () => {
      clientFetchGenerationRef.current += 1;
      if (clientFetchControllerRef.current) {
        clientFetchControllerRef.current.abort();
        clientFetchControllerRef.current = null;
      }
    };
  }, [clientId]);

  // Stale-hint consumption (mirrors ClientsListScreen).
  //
  // React to location.state.mayBeStale once per navigation. Consume by
  // re-navigating to the same path with state=null so forward/back and
  // browser refresh do NOT replay the signal. No child route currently
  // emits this signal in Phase R2 — the consumption side is wired so
  // future phases (when children become modal/return-aware) can opt
  // in without re-discovering the protocol.
  useEffect(() => {
    if (location.state && location.state.mayBeStale) {
      setStale(true);
      setStaleReason(
        location.state.reason ||
        "Дані клієнта могли застаріти після редагування"
      );
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: null }
      );
    }
  }, [location, navigate]);

  // Phase S7 — T-4 destructive workflow handlers (R8 §4 T-4).
  //
  // The success path is navigation + mayBeStale. There is no
  // section refresh (the section unmounts with the route), no
  // optimistic eviction from the /clients list (R6-15 / §6.5
  // rule 4), no event emit (§6.5 rule 6), and no entity-store
  // mutation (R8 R-7). The replace:true is load-bearing —
  // without it the browser back button would return to a 404 URL.
  //
  // Error path: stay on the surface, surface the error in the
  // confirmation modal. The entity still exists (or the user
  // lacks permission); the next 403/404 from backend is the
  // authoritative permission signal (R6-14, §6.5 rule 8).
  const handleDeleteRequest = () => {
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const handleDeleteCancel = () => {
    if (deleting) return;
    setDeleteOpen(false);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    // R8.7 B-1 — synchronous ref guard closes the rapid-double-click
    // window before React re-renders the state-derived disable. On
    // success the component unmounts on navigate so the ref is
    // released only on the failure branch (mirrors setDeleting(false)
    // intent below).
    //
    // R8.7.4 — Replaced `state.mayBeStale` navigation hint with a
    // success toast. /clients re-fetches on mount; the deleted client
    // is gone; the prior "list may be stale" banner overclaimed
    // uncertainty. Edit-path mayBeStale emission to /clients/:id is
    // intentionally untouched (different semantic — EDIT keeps
    // freshness ambiguity during the re-fetch window).
    if (deletingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteClientById(clientId);
      toast.success("Клієнта видалено.");
      navigate("/clients", { replace: true });
    } catch (err) {
      deletingRef.current = false;
      setDeleting(false);
      setDeleteError(messageFromError(err));
    }
  };

  // Phase R4 — top-level reload refreshes ONLY the client core.
  // Sections own their own refresh buttons for sub-resource data.
  // This boundary is deliberate: a single "refresh everything" path
  // would re-introduce the universal-preload coupling we just removed.
  const handleReload = async () => {
    if (refreshingRef.current) return;
    if (clientFetchControllerRef.current) {
      clientFetchControllerRef.current.abort();
    }
    const controller = new AbortController();
    clientFetchControllerRef.current = controller;
    const generation = ++clientFetchGenerationRef.current;

    refreshingRef.current = true;
    setRefreshing(true);
    setRefreshError(null);
    try {
      const clientData = await fetchClientById(clientId, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (generation !== clientFetchGenerationRef.current) return;
      const reconciled = preferencesReconcileRef.current;
      if (reconciled) {
        preferencesReconcileRef.current = null;
        setClient({
          ...clientData,
          preferences: reconciled.value == null ? null : reconciled.value,
        });
      } else {
        setClient(clientData);
      }
      setLoadState("loaded");
      setStale(false);
      setStaleReason(null);
    } catch (err) {
      if (controller.signal.aborted) return;
      if (generation !== clientFetchGenerationRef.current) return;
      setRefreshError(messageFromError(err));
    } finally {
      if (clientFetchControllerRef.current === controller) {
        clientFetchControllerRef.current = null;
      }
      if (generation === clientFetchGenerationRef.current) {
        refreshingRef.current = false;
        setRefreshing(false);
      }
    }
  };

  const handlePreferencesSaved = (saved) => {
    // Close modal immediately after server-confirmed success.
    setPrefsEditorOpen(false);
    const nextPreferences = saved == null ? null : saved;
    preferencesReconcileRef.current = { value: nextPreferences };
    // Invalidate any in-flight client refresh so a late stale payload
    // cannot overwrite the just-confirmed preferences snapshot.
    clientFetchGenerationRef.current += 1;
    if (clientFetchControllerRef.current) {
      clientFetchControllerRef.current.abort();
      clientFetchControllerRef.current = null;
    }
    refreshingRef.current = false;
    setRefreshing(false);
    // Synchronous reconcile — matching flow starts from this truth
    // without requiring a second mandatory GET.
    setClient((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        preferences: nextPreferences,
      };
    });
    setPreferencesRevision((n) => n + 1);
    // Optional authoritative reload (guarded); must not clobber newer prefs.
    void handleReload();
  };

  if (loadState === "loading") {
    return (
      <div className="feature-client-detail__state">
        <Spinner size="lg" label="Завантаження клієнта" />
      </div>
    );
  }

  if (loadState === "not-found") {
    return (
      <div className="feature-client-detail__state">
        <EmptyState
          icon="fa-solid fa-user-slash"
          title="Клієнта не знайдено"
          description="Можливо, цей клієнт був видалений або у вас немає доступу."
          size="md"
          primaryAction={
            <Link
              to="/clients"
              className="action-btn"
            >
              До списку клієнтів
            </Link>
          }
        />
      </div>
    );
  }

  if (loadState === "forbidden") {
    return (
      <div className="feature-client-detail__state">
        <EmptyState
          icon="fa-solid fa-lock"
          title="Немає доступу до клієнта"
          description="У вас немає прав переглядати цього клієнта."
          size="md"
          primaryAction={
            <Link
              to="/clients"
              className="action-btn"
            >
              До списку клієнтів
            </Link>
          }
        />
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div className="feature-client-detail__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Не вдалося завантажити клієнта"
          description={loadError || "Спробуйте оновити сторінку."}
          size="md"
          primaryAction={
            <button
              type="button"
              className="action-btn"
              onClick={handleReload}
              disabled={refreshing}
              data-loading={refreshing ? "true" : undefined}
            >
              {refreshing ? (
                <Spinner size="sm" label="Оновлення" />
              ) : (
                "Повторити"
              )}
            </button>
          }
        />
      </div>
    );
  }

  const typeKey = client && client.type;
  const statusKey = client && client.status;
  const typeLabel = TYPE_LABELS[typeKey] || typeKey || null;
  const typeIcon = TYPE_ICONS[typeKey] || null;
  const statusLabel = STATUS_LABELS[statusKey] || statusKey || null;
  const canEdit = client && client.can_edit !== false;
  // Destructive workflow gating: only the client's owner may delete
  // (mirrors backend IsClientOwnerOnly on ClientViewSet.destroy).
  // is_owner is computed server-side from request.user.id vs
  // owner_id and returned on ClientDetailSerializer.
  const canDelete = Boolean(client && client.is_owner);

  return (
    <div className="feature-client-detail__body" data-feature="client-detail">
      <Link to="/clients" className="cd-back">
        <i className="fa-solid fa-arrow-left" aria-hidden="true" />
        <span>До списку клієнтів</span>
      </Link>

      {stale ? (
        <div
          className="feature-client-detail__stale"
          role="status"
          aria-live="polite"
        >
          <div className="feature-client-detail__stale-text">
            <i
              className="fa-solid fa-triangle-exclamation"
              aria-hidden="true"
            />
            <span>{staleReason || "Дані можуть бути застарілими."}</span>
          </div>
          <button
            type="button"
            className="feature-client-detail__refresh-btn"
            onClick={handleReload}
            disabled={refreshing}
            data-loading={refreshing ? "true" : undefined}
          >
            {refreshing ? (
              <Spinner size="sm" label="Оновлення" />
            ) : (
              <>
                <i
                  className="fa-solid fa-arrows-rotate"
                  aria-hidden="true"
                />
                <span>Оновити</span>
              </>
            )}
          </button>
        </div>
      ) : null}

      <header
        className="cd-header"
        aria-labelledby="client-detail-name"
      >
        <div className="cd-header__main">
          <span
            className={`avatar avatar--xl ${avatarColorClass(client.id)}`}
            aria-hidden="true"
          >
            {initialsOf(client)}
          </span>
          <div className="cd-header__id">
            <h1
              id="client-detail-name"
              className="cd-header__name"
            >
              {fullnameOf(client)}
            </h1>
            <div className="cd-header__pills">
              {typeLabel ? (
                <span
                  className={`type-pill type-pill--${typeKey || "other"
                    }`}
                >
                  {typeIcon ? (
                    <i className={typeIcon} aria-hidden="true" />
                  ) : null}
                  <span>{typeLabel}</span>
                </span>
              ) : null}
              {statusLabel ? (
                <span
                  className={`status-pill status-pill--${statusKey || "lead"
                    }`}
                >
                  <span className="dot" />
                  <span>{statusLabel}</span>
                </span>
              ) : null}
            </div>
            <p className="cd-header__meta">
              {client.source ? (
                <span>
                  <i className="fa-solid fa-tag" aria-hidden="true" />
                  <span>{client.source}</span>
                </span>
              ) : null}
              <span>
                <i className="fa-solid fa-fingerprint" aria-hidden="true" />
                <span>ID: {truncateId(client.id)}</span>
              </span>
            </p>
          </div>
        </div>

        <div className="cd-header__actions">
          {canEdit ? (
            <button
              type="button"
              className="action-btn"
              // className="feature-client-detail__action-btn"
              onClick={() => setEditOpen(true)}
            >
              <i className="fa-solid fa-pen-to-square" aria-hidden="true" />
              <span>Редагувати</span>
            </button>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              className="action-btn"
              onClick={() => {
                viewingsSectionRef.current?.openViewingModal();
              }}
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span>Запланувати перегляд</span>
            </button>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              className="action-btn"
              // className="feature-client-detail__action-btn"
              onClick={() => {
                activitySectionRef.current?.openCommunicationModal();
              }}
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span>Залогувати дзвінок</span>
            </button>
          ) : null}
          {canEdit ? (
            <button
              type="button"
              className="action-btn"
              // className="feature-client-detail__action-btn"
              onClick={() => {
                tasksSectionRef.current?.openTaskModal();
              }}
            >
              <i className="fa-solid fa-list-check" aria-hidden="true" />
              <span>Нова задача</span>
            </button>
          ) : null}
          {/* Phase B3 — T-3 alias trigger for the sidebar shares card.
             Owner-only (mirrors backend IsClientOwnerOnly on all four
             share endpoints; B2 F-F). The bridge is deletable — the
             sidebar card has its own "+" trigger in its head. */}
          {canDelete ? (
            <button
              type="button"
              className="action-btn"
              // className="feature-client-detail__action-btn"
              onClick={() => {
                sharesCardRef.current?.openShareModal();
              }}
            >
              <i className="fa-solid fa-share-nodes" aria-hidden="true" />
              <span>Поділитися</span>
            </button>
          ) : null}
          {canDelete ? (
            <button
              type="button"
              className="action-btn action-btn--danger"
              // className="feature-client-detail__action-btn feature-client-detail__action-btn--danger"
              onClick={handleDeleteRequest}
            >
              <i className="fa-solid fa-trash" aria-hidden="true" />
              <span>Видалити</span>
            </button>
          ) : null}
          <button
            type="button"
            className="feature-client-detail__refresh-icon-btn"
            onClick={handleReload}
            disabled={refreshing}
            data-loading={refreshing ? "true" : undefined}
            aria-label="Оновити клієнта"
            title="Оновити"
          >
            {refreshing ? (
              <Spinner size="sm" label="Оновлення" />
            ) : (
              <i
                className="fa-solid fa-arrows-rotate"
                aria-hidden="true"
              />
            )}
          </button>
        </div>
      </header>

      {refreshError ? (
        <div className="feature-client-detail__refresh-error" role="alert">
          {refreshError}
        </div>
      ) : null}

      <div className="cd-grid">
        <div className="cd-main">
          <ClientContactsSection
            client={client}
            clientId={clientId}
            canEdit={canEdit}
            onEditCoreContacts={() => setEditOpen(true)}
          />
          <ClientPreferencesSection
            client={client}
            canEdit={canEdit}
            editorOpen={prefsEditorOpen}
            onRequestEditPreferences={() => setPrefsEditorOpen(true)}
            onCloseEditor={() => setPrefsEditorOpen(false)}
            onPreferencesSaved={handlePreferencesSaved}
          />
          <ClientMatchesSection
            clientId={clientId}
            canEdit={canEdit}
            client={client}
            preferencesRevision={preferencesRevision}
            onRequestEditPreferences={() => setPrefsEditorOpen(true)}
          />
          <ClientViewingsSection
            ref={viewingsSectionRef}
            clientId={clientId}
            canEdit={canEdit}
          />
          <ClientActivitySection
            ref={activitySectionRef}
            clientId={clientId}
          />
          <ClientTasksSection
            ref={tasksSectionRef}
            clientId={clientId}
          />
        </div>
        <aside className="cd-sidebar">
          <ClientNotesCard
            client={client}
            onEditRequest={canEdit ? () => setEditOpen(true) : undefined}
          />
          <PersonalTransferPreviewCard
            clientId={clientId}
            isOwner={Boolean(client?.is_owner)}
          />
          <ClientSharesCard
            ref={sharesCardRef}
            client={client}
            clientId={clientId}
          />
          <ClientPresentationsCard client={client} clientId={clientId} />
          <ClientStampsCard client={client} />
        </aside>
      </div>

      <ClientEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        clientId={clientId}
        onSaved={() => {
          setEditOpen(false);
          handleReload();
        }}
      />

      <ClientDeleteConfirmModal
        open={deleteOpen}
        clientName={fullnameOf(client)}
        deleting={deleting}
        errorMessage={deleteError}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}

export default ClientDetailScreenContent;
